/**
 * AffiliateNotesPage — internal notes + tag chips per affiliate.
 *
 * An operator picks an affiliate ID, then edits two sub-surfaces:
 *   1. Notes — short text rows (<=4000 chars), pinnable, created-at sort.
 *   2. Tags  — short slug chips with a color token (zinc|red|amber|emerald|sky|violet|rose).
 *
 * Styling follows the Trackerity dashboard brief:
 *   - light theme only, brand-green (emerald) accents only on the page heading
 *   - 6-8px rounded corners, zinc borders, subtle shadows
 *   - no emojis, no competitor naming
 */
import { useState, type FormEvent } from "react";
import {
	useAffiliateNotesList,
	useAffiliateNoteCreate,
	useAffiliateNoteDelete,
	useAffiliateTagsList,
	useAffiliateTagCreate,
	useAffiliateTagDelete,
} from "./hooks";
import { TAG_COLORS, type TagColor } from "./api";

// ---------------------------------------------------------------------------
// style tokens (light theme, brand-green accents only)
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const TEXTAREA =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-medium text-zinc-600";

const COLOR_CHIP: Record<TagColor, string> = {
	zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
	red: "bg-red-50 text-red-700 border-red-200",
	amber: "bg-amber-50 text-amber-800 border-amber-200",
	emerald: "bg-green-50 text-green-800 border-green-200",
	sky: "bg-sky-50 text-sky-800 border-sky-200",
	violet: "bg-green-50 text-green-800 border-green-200",
	rose: "bg-rose-50 text-rose-800 border-rose-200",
};

function chipClass(raw: string): string {
	const key = (raw as TagColor) in COLOR_CHIP ? (raw as TagColor) : "zinc";
	return COLOR_CHIP[key];
}

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

export function AffiliateNotesPage() {
	const [affiliateIdInput, setAffiliateIdInput] = useState("");
	const [affiliateId, setAffiliateId] = useState("");

	const [noteBody, setNoteBody] = useState("");
	const [notePinned, setNotePinned] = useState(false);

	const [tagInput, setTagInput] = useState("");
	const [tagColor, setTagColor] = useState<TagColor>("zinc");

	const notesQuery = useAffiliateNotesList(affiliateId);
	const createNote = useAffiliateNoteCreate();
	const deleteNote = useAffiliateNoteDelete(affiliateId);

	const tagsQuery = useAffiliateTagsList(affiliateId);
	const createTag = useAffiliateTagCreate();
	const deleteTag = useAffiliateTagDelete(affiliateId);

	const selectAffiliate = (e: FormEvent) => {
		e.preventDefault();
		const v = affiliateIdInput.trim();
		if (!v) return;
		setAffiliateId(v);
	};

	const submitNote = (e: FormEvent) => {
		e.preventDefault();
		const body = noteBody.trim();
		if (!body || !affiliateId) return;
		createNote.mutate(
			{ affiliate_id: affiliateId, body, pinned: notePinned },
			{
				onSuccess: () => {
					setNoteBody("");
					setNotePinned(false);
				},
			},
		);
	};

	const submitTag = (e: FormEvent) => {
		e.preventDefault();
		const tag = tagInput.trim();
		if (!tag || !affiliateId) return;
		createTag.mutate(
			{ affiliate_id: affiliateId, tag, color: tagColor },
			{ onSuccess: () => setTagInput("") },
		);
	};

	const notes = notesQuery.data?.items ?? [];
	const tags = tagsQuery.data?.items ?? [];

	return (
		<main
			data-testid="affiliate-notes-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					affiliate-notes
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-3xl font-semibold text-transparent">
					Affiliate notes &amp; tags
				</h1>
				<p className="text-sm text-zinc-600">
					Internal notes and color-tagged chips for a publisher. Private to
					your tenant.
				</p>
			</header>

			<section className={CARD} aria-label="Select affiliate">
				<h2 className="text-base font-semibold text-zinc-900">Affiliate</h2>
				<form
					onSubmit={selectAffiliate}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="affiliate-notes-select-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Affiliate ID"
						value={affiliateIdInput}
						onChange={(e) => setAffiliateIdInput(e.currentTarget.value)}
						data-testid="affiliate-notes-affiliate-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={!affiliateIdInput.trim()}
						data-testid="affiliate-notes-select-submit"
					>
						Load
					</button>
				</form>
				{affiliateId ? (
					<p className="mt-2 text-xs text-zinc-500" data-testid="affiliate-notes-current-id">
						Showing records for <span className="font-mono text-zinc-700">{affiliateId}</span>
					</p>
				) : null}
			</section>

			{affiliateId ? (
				<>
					<section className={CARD} aria-label="Add note">
						<h2 className="text-base font-semibold text-zinc-900">Add note</h2>
						<form
							onSubmit={submitNote}
							className="mt-3 flex flex-col gap-3"
							data-testid="affiliate-notes-note-form"
						>
							<label className="flex flex-col gap-1">
								<span className={LABEL}>Body</span>
								<textarea
									className={TEXTAREA}
									rows={3}
									maxLength={4000}
									placeholder="Short internal note"
									value={noteBody}
									onChange={(e) => setNoteBody(e.currentTarget.value)}
									disabled={createNote.isPending}
									data-testid="affiliate-notes-note-body"
								/>
							</label>
							<div className="flex items-center justify-between">
								<label className="flex items-center gap-2 text-sm text-zinc-700">
									<input
										type="checkbox"
										className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-500"
										checked={notePinned}
										onChange={(e) => setNotePinned(e.currentTarget.checked)}
										disabled={createNote.isPending}
										data-testid="affiliate-notes-note-pinned"
									/>
									<span>Pin to top</span>
								</label>
								<button
									type="submit"
									className={BTN}
									disabled={createNote.isPending || !noteBody.trim()}
									data-testid="affiliate-notes-note-submit"
								>
									{createNote.isPending ? "Saving." : "Save note"}
								</button>
							</div>
						</form>
						{createNote.error ? (
							<p className="mt-2 text-xs text-red-600" role="alert">
								{createNote.error.message}
							</p>
						) : null}
					</section>

					<section className={CARD} aria-label="Notes">
						<h2 className="text-base font-semibold text-zinc-900">Notes</h2>
						{notesQuery.isLoading ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="affiliate-notes-notes-loading"
							>
								Loading.
							</p>
						) : notesQuery.error ? (
							<p className="mt-3 text-sm text-red-600" role="alert">
								{notesQuery.error.message}
							</p>
						) : notes.length === 0 ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="affiliate-notes-notes-empty"
							>
								No notes yet.
							</p>
						) : (
							<ul
								className="mt-3 divide-y divide-zinc-200"
								data-testid="affiliate-notes-notes-list"
							>
								{notes.map((n) => (
									<li
										key={n.id}
										className="flex items-start justify-between gap-4 py-3"
										data-testid={`affiliate-notes-note-row-${n.id}`}
									>
										<div className="flex flex-col gap-1">
											<div className="flex items-center gap-2 text-xs text-zinc-500">
												<span className="font-mono">{n.id}</span>
												{n.pinned ? (
													<span className="rounded-[4px] border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
														pinned
													</span>
												) : null}
												<span>{new Date(n.created_at * 1000).toLocaleString()}</span>
											</div>
											<p className="whitespace-pre-wrap text-sm text-zinc-800">{n.body}</p>
										</div>
										<button
											type="button"
											className={BTN_GHOST}
											onClick={() => deleteNote.mutate(n.id)}
											disabled={deleteNote.isPending}
											data-testid={`affiliate-notes-note-delete-${n.id}`}
										>
											Delete
										</button>
									</li>
								))}
							</ul>
						)}
					</section>

					<section className={CARD} aria-label="Tags">
						<h2 className="text-base font-semibold text-zinc-900">Tags</h2>
						<form
							onSubmit={submitTag}
							className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end"
							data-testid="affiliate-notes-tag-form"
						>
							<label className="flex flex-1 flex-col gap-1">
								<span className={LABEL}>Tag</span>
								<input
									type="text"
									className={INPUT}
									placeholder="e.g. priority-a"
									value={tagInput}
									onChange={(e) => setTagInput(e.currentTarget.value)}
									disabled={createTag.isPending}
									data-testid="affiliate-notes-tag-input"
								/>
							</label>
							<label className="flex flex-col gap-1 sm:w-36">
								<span className={LABEL}>Color</span>
								<select
									className={INPUT}
									value={tagColor}
									onChange={(e) => setTagColor(e.currentTarget.value as TagColor)}
									disabled={createTag.isPending}
									data-testid="affiliate-notes-tag-color"
								>
									{TAG_COLORS.map((c) => (
										<option key={c} value={c}>
											{c}
										</option>
									))}
								</select>
							</label>
							<button
								type="submit"
								className={BTN}
								disabled={createTag.isPending || !tagInput.trim()}
								data-testid="affiliate-notes-tag-submit"
							>
								{createTag.isPending ? "Saving." : "Add tag"}
							</button>
						</form>
						{createTag.error ? (
							<p className="mt-2 text-xs text-red-600" role="alert">
								{createTag.error.message}
							</p>
						) : null}

						<div className="mt-4">
							{tagsQuery.isLoading ? (
								<p
									className="text-sm text-zinc-500"
									data-testid="affiliate-notes-tags-loading"
								>
									Loading.
								</p>
							) : tagsQuery.error ? (
								<p className="text-sm text-red-600" role="alert">
									{tagsQuery.error.message}
								</p>
							) : tags.length === 0 ? (
								<p
									className="text-sm text-zinc-500"
									data-testid="affiliate-notes-tags-empty"
								>
									No tags yet.
								</p>
							) : (
								<ul
									className="flex flex-wrap gap-2"
									data-testid="affiliate-notes-tags-list"
								>
									{tags.map((t) => (
										<li
											key={t.id}
											className={`flex items-center gap-1 rounded-[6px] border px-2 py-1 text-xs font-medium ${chipClass(t.color)}`}
											data-testid={`affiliate-notes-tag-row-${t.id}`}
										>
											<span>{t.tag}</span>
											<button
												type="button"
												aria-label={`Remove ${t.tag}`}
												onClick={() => deleteTag.mutate(t.id)}
												disabled={deleteTag.isPending}
												className="ml-1 text-zinc-500 hover:text-zinc-800 disabled:opacity-60"
												data-testid={`affiliate-notes-tag-delete-${t.id}`}
											>
												x
											</button>
										</li>
									))}
								</ul>
							)}
						</div>
					</section>
				</>
			) : (
				<section className={CARD} aria-label="Awaiting affiliate selection">
					<p
						className="text-sm text-zinc-500"
						data-testid="affiliate-notes-awaiting"
					>
						Enter an affiliate ID above to load notes and tags.
					</p>
				</section>
			)}
		</main>
	);
}

export default AffiliateNotesPage;
